import React, { useState, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import Select, { components } from "react-select";
import {
    ThemeProvider,
    createTheme,
    Popover,
    Box
} from "@mui/material";
import {
    LocalizationProvider,
    DateCalendar,
    PickersDay
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/he";

import { styleOptions, regionOptions } from "../data/TravelOptions";
import "./trip-filter-bar.css";

dayjs.locale("he");

const theme = createTheme({
    direction: "rtl",
    typography: {
        fontFamily: "Varela Round, sans-serif"
    }
});

// ✅ This version works with your existing CSS
const SingleSummaryContainer = ({ children, ...props }) => {
    const selected = props.getValue();
    const placeholder = props.selectProps.placeholder;
    let summary = "";

    if (selected.length === 0) {
        summary = placeholder;
    } else if (selected.length === 1) {
        summary = selected[0].label;
    } else {
        summary = `נבחרו ${selected.length}`;
    }

    return (
        <components.ValueContainer {...props}>
            <div className="summary-label">{summary}</div>
        </components.ValueContainer>
    );
};

const TripFilter = ({ onFilterChange }) => {
    const [selectedStyles, setSelectedStyles] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (onFilterChange) {
            onFilterChange({
                style: selectedStyles,
                region: selectedRegions,
                dates: selectedDates
            });
        }
    }, [selectedStyles, selectedRegions, selectedDates]);

    const toggleDateSelection = (date) => {
        const exists = selectedDates.some((d) => d.isSame(date, "day"));
        if (exists) {
            setSelectedDates((prev) =>
                prev.filter((d) => !d.isSame(date, "day"))
            );
        } else {
            setSelectedDates((prev) => [...prev, date]);
        }
    };

    const handleOpenCalendar = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseCalendar = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const getDateLabel = () => {
        if (selectedDates.length === 0) return "בחר תאריך";
        if (selectedDates.length === 1) {
            return selectedDates[0].format("DD/MM/YYYY");
        }
        return `נבחרו ${selectedDates.length} תאריכים`;
    };

    return (
        <ThemeProvider theme={theme}>
            <div className="trip-filter-wrapper" dir="rtl">
                <div className="trip-filter-bar">
                    <div className="trip-filter-section">
                        <div>סגנון הטיול</div>
                        <Select
                            isMulti
                            hideSelectedOptions={false}
                            options={styleOptions}
                            value={selectedStyles}
                            onChange={setSelectedStyles}
                            className="wide-select"
                            classNamePrefix="react-select"
                            placeholder="בחר סגנון"
                            components={{
                                ValueContainer: SingleSummaryContainer,
                                MultiValue: () => null,
                                MultiValueRemove: () => null
                            }}
                        />
                    </div>

                    <div className="trip-filter-divider" />

                    <div className="trip-filter-section">
                        <div>אזור הטיול</div>
                        <Select
                            isMulti
                            hideSelectedOptions={false}
                            options={regionOptions}
                            value={selectedRegions}
                            onChange={setSelectedRegions}
                            className="wide-select"
                            classNamePrefix="react-select"
                            placeholder="בחר אזור"
                            components={{
                                ValueContainer: SingleSummaryContainer,
                                MultiValue: () => null,
                                MultiValueRemove: () => null
                            }}
                        />
                    </div>

                    <div className="trip-filter-divider" />

                    <div className="trip-filter-section">
                        <div>תאריכי הטיול</div>
                        <div className="custom-datepicker-wrapper">
                            <div className="custom-date-input" onClick={handleOpenCalendar}>
                                <div className="summary-label">
                                    {getDateLabel()}
                                    {selectedDates.length > 0 && (
                                        <button
                                            className="clear-dates-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedDates([]);
                                            }}
                                            title="נקה תאריכים"
                                        >
                                            ×
                                        </button>
                                    )}
                                    <FaChevronDown className="date-arrow-icon" />
                                </div>
                            </div>

                            <Popover
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleCloseCalendar}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{
                                    sx: {
                                        mt: 1,
                                        width: '350px !important',
                                        maxWidth: '350px !important',
                                        height: '340px !important',
                                        borderRadius: '16px',
                                        maxHeight: '400px',
                                        padding: '0px !important',
                                    }
                                }}
                            >
                                <Box sx={{ margin: 0, padding: 0, width: "100%", height: "100%", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale="he"
                                    >
                                        <DateCalendar
                                            value={null}
                                            onChange={toggleDateSelection}
                                            slots={{
                                                day: (props) => {
                                                    const isSelected = selectedDates.some((d) =>
                                                        d.isSame(props.day, "day")
                                                    );
                                                    return (
                                                        <PickersDay
                                                            {...props}
                                                            disableMargin
                                                            sx={{
                                                                "&:hover": {
                                                                    bgcolor: "#e7efe3"
                                                                },
                                                                ...(isSelected && {
                                                                    bgcolor: "#1B5C1C !important",
                                                                    color: "#fff !important"
                                                                }),
                                                                ...(dayjs().isSame(props.day, "day") && {
                                                                    border: "1px solid #1B5C1C",
                                                                    bgcolor: "transparent"
                                                                })
                                                            }}
                                                        />
                                                    );
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>
                            </Popover>
                        </div>
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );
};

export default TripFilter;
